import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useAppNavigate, useBackToParent } from '@/hooks'
import { importBatchesApi } from '@/services'
import { isHandledAuthError } from '@/services/client'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { useToast } from '@/widgets/common'
import DetailErrorSummary, { type DetailErrorEntry } from '@/widgets/common/DetailErrorSummary'
import { detailCellInputClass } from '@/widgets/common/detailCellStyles'
import { RECIPIENT_BOOLEAN_FIELD_KEYS, RECIPIENT_CSV_LABELS } from '@/data/recipientFields'
import { BackButton } from '@/widgets/demo/ListPrimitives'
import type { ImportBatchResponse, ImportErrorResponse } from '@/services/importBatches/types'

interface ErrorRow {
  lineNo: number
  cells: string[]
  initialCells: string[]
  errorColIndexes: number[]
  /** バック側エラー理由 (colIdx → 日本語メッセージ)。値未編集時のみ採用される。 */
  backendReasons: Record<number, string>
  /** バック側エラーコード (colIdx → code)。旧エラー表示の抑制判定に使う。 */
  backendErrorCodes: Record<number, string>
}

// バックエンド (CsvImportService.buildFieldSpecs / RecipientService.parseShort/parseLong) と同じ規則。
const SHORT_FIELD_KEYS = new Set([
  'f2','f3','f4','f5','f6','f7','f8','f9','f10',
  'f19','f21','f22','f23','f24','f79',
])
const LONG_FIELD_KEYS = new Set([
  'f31','f32','f33','f34','f35','f36','f37','f38','f39','f40','f41',
  'f42','f43','f44','f45','f46','f47','f48','f49','f50','f51',
])
const STRING_FIELD_MAX_LENGTH: Record<string, number> = {
  f1: 255,
  f13: 64, f14: 64, f15: 64, f16: 64, f18: 64, f20: 64,
  f25: 64, f26: 64, f27: 64, f28: 64, f29: 64, f30: 64,
  f52: 256, f53: 16, f54: 512, f55: 256,
  f57: 256, f58: 256, f59: 256,
  f60: 64, f61: 64, f62: 64, f63: 64, f64: 64, f65: 64,
  f77: 128, f78: 128, f80: 32, f81: 128,
}
const SHORT_MIN = -32768
const SHORT_MAX = 32767
const LONG_MIN = -9223372036854775808n
const LONG_MAX = 9223372036854775807n
const ERROR_CODE_HOUSEHOLD_MISMATCH = 'CSV_HOUSEHOLD_FIELD_MISMATCH'

function isValidShort(v: string): boolean {
  if (!/^-?\d+$/.test(v)) return false
  const n = Number(v)
  return Number.isInteger(n) && n >= SHORT_MIN && n <= SHORT_MAX
}

function isValidLong(v: string): boolean {
  if (!/^-?\d+$/.test(v)) return false
  try {
    const n = BigInt(v)
    return n >= LONG_MIN && n <= LONG_MAX
  } catch {
    return false
  }
}

function validateCell(colIdx: number, raw: string): string | null {
  const value = raw ?? ''
  const trimmed = value.trim()
  if (trimmed === '') return null
  const fieldKey = `f${colIdx + 1}`
  if (SHORT_FIELD_KEYS.has(fieldKey) && !isValidShort(trimmed)) {
    return `${SHORT_MIN}〜${SHORT_MAX} の整数を半角数字で入力してください`
  }
  if (LONG_FIELD_KEYS.has(fieldKey) && !isValidLong(trimmed)) {
    return '整数を半角数字で入力してください'
  }
  if (RECIPIENT_BOOLEAN_FIELD_KEYS.has(fieldKey) && !/^(true|false|0|1)$/i.test(trimmed)) {
    return 'true/false または 0/1 を入力してください'
  }
  const max = STRING_FIELD_MAX_LENGTH[fieldKey]
  if (max != null && value.length > max) {
    return `${max}文字以内で入力してください（現在 ${value.length}文字）`
  }
  return null
}

export default function ImportErrorEdit() {
  const { batchId: batchIdParam } = useParams<{ batchId: string }>()
  const navigate = useAppNavigate()
  const goBack = useBackToParent()
  const toast = useToast()
  const parsedBatchId = Number(batchIdParam)
  const batchId = Number.isInteger(parsedBatchId) && parsedBatchId > 0 ? parsedBatchId : null

  const [saving, setSaving] = useState(false)
  const [batch, setBatch] = useState<ImportBatchResponse | null>(null)
  const [rows, setRows] = useState<ErrorRow[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!batchId) {
      toast.warning('取込バッチが指定されていません。')
      navigate('ocr-read-list')
      return
    }

    // 取込エラー明細を取得する正当なデータ取得 effect。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    let cancelled = false

    const load = async () => {
      try {
        const [batchRes, errorsRes] = await Promise.all([
          importBatchesApi.getById(batchId),
          importBatchesApi.getErrors(batchId),
        ])

        if (cancelled) return

        const nextRows = buildRows(errorsRes.data)
        if ((batchRes.data.errorCount ?? 0) <= 0 || nextRows.length === 0) {
          toast.info('この取込バッチに修正対象のエラーはありません。')
          navigate('ocr-read-list')
          return
        }

        setBatch(batchRes.data)
        setRows(nextRows)
      } catch (error) {
        if (!cancelled && !isHandledAuthError(error)) {
          navigate('ocr-read-list')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [batchId, navigate, toast])

  const updateCell = useCallback((rowIdx: number, colIdx: number, val: string) => {
    setRows((prev) =>
      prev.map((row, currentRowIdx) =>
        currentRowIdx === rowIdx
          ? { ...row, cells: row.cells.map((cell, currentColIdx) => (currentColIdx === colIdx ? val : cell)) }
          : row,
      ),
    )
  }, [])

  /**
   * 各セルに対する有効なエラーメッセージを返す:
   * - フロント検証で不正 → フロント側メッセージ
   * - 値が初期値のまま → バック側メッセージ
   * - それ以外 → null（エラー解消）
   */
  const cellErrors = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = {}
    rows.forEach((row, rowIdx) => {
      const cells = row.cells
      for (let colIdx = 0; colIdx < cells.length; colIdx += 1) {
        const cur = cells[colIdx] ?? ''
        const frontErr = validateCell(colIdx, cur)
        if (frontErr) {
          out[`${rowIdx}-${colIdx}`] = frontErr
          continue
        }
        const initial = row.initialCells[colIdx] ?? ''
        if (
          cur.trim() === '' &&
          row.backendErrorCodes[colIdx] === ERROR_CODE_HOUSEHOLD_MISMATCH
        ) {
          continue
        }
        if (cur === initial && row.backendReasons[colIdx]) {
          out[`${rowIdx}-${colIdx}`] = row.backendReasons[colIdx]
        }
      }
    })
    return out
  }, [rows])

  const errorSummary = useMemo<DetailErrorEntry[]>(() => {
    const out: DetailErrorEntry[] = []
    rows.forEach((row, rowIdx) => {
      for (let colIdx = 0; colIdx < row.cells.length; colIdx += 1) {
        const msg = cellErrors[`${rowIdx}-${colIdx}`]
        if (msg) {
          out.push({
            key: `${rowIdx}-${colIdx}`,
            lineNo: row.lineNo,
            label: RECIPIENT_CSV_LABELS[colIdx] || `f${colIdx + 1}`,
            message: msg,
          })
        }
      }
    })
    return out
  }, [rows, cellErrors])

  const handleSave = useCallback(async () => {
    if (!batchId || rows.length === 0) return

    setSaving(true)
    try {
      const res = await importBatchesApi.fixErrors(
        batchId,
        rows.map((row) => ({ lineNo: row.lineNo, cells: row.cells })),
      )
      const updatedBatch = res.data

      if (updatedBatch.errorCount != null && updatedBatch.errorCount > 0) {
        toast.warning(`${updatedBatch.errorCount} 件のエラーが残っています。修正を続けてください。`)
        const [batchRes, errorsRes] = await Promise.all([
          importBatchesApi.getById(batchId),
          importBatchesApi.getErrors(batchId),
        ])
        const nextRows = buildRows(errorsRes.data)

        if ((batchRes.data.errorCount ?? 0) <= 0 || nextRows.length === 0) {
          toast.info('この取込バッチに修正対象のエラーはありません。')
          navigate('ocr-read-list')
          return
        }

        setBatch(batchRes.data)
        setRows(nextRows)
      } else {
        toast.success('エラー修正を保存しました')
        navigate('ocr-read-list')
      }
    } catch (error) {
      if (isHandledAuthError(error)) return
    } finally {
      setSaving(false)
    }
  }, [batchId, rows, navigate, toast])

  if (!batchId) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-500">一覧へ戻っています...</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-500">読み込み中...</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader breadcrumbs={[{ label: 'データ取込', page: 'ocr-read-list' }, { label: 'エラー修正' }]} />
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <BackButton onClick={goBack} />

        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap justify-between items-start gap-3 bg-gray-50">
            <div>
              <h2 className="text-lg font-bold text-gray-900">エラー修正</h2>
              {batch && (
                <p className="text-sm text-gray-600 mt-1">
                  取込番号 <span className="font-mono font-medium text-gray-800">{batch.importNo}</span>
                  <span className="mx-2 text-gray-300">|</span>
                  取込タイプ <span className="font-medium">{batch.importType}</span>
                  <span className="mx-2 text-gray-300">|</span>
                  エラー数 <span className="font-mono font-medium">{batch.errorCount}</span>
                </p>
              )}
            </div>
          </div>

          <div className="px-6 py-3 bg-amber-50 border-b border-amber-100 text-sm text-amber-950">
            受給者情報の取込エラーを行単位で修正できます。赤く表示された項目を確認し、保存すると再検証されます。
          </div>

          <div className="px-6 py-4">
            <p className="text-xs text-gray-500 mb-2">
              <span className="font-mono font-medium text-gray-700">{RECIPIENT_CSV_LABELS.length}</span>
              列を表示 / 対象
              <span className="font-mono font-medium text-gray-700">{rows.length}</span>
              行
            </p>
            <div className="max-h-[min(70vh,36rem)] rounded-lg border border-gray-200 overflow-auto overscroll-contain [scrollbar-gutter:stable]">
              <table className="min-w-max text-xs border-separate border-spacing-0">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th
                      scope="col"
                      className="sticky top-0 left-0 z-30 box-border text-left font-semibold text-gray-800 px-2 py-2 bg-gray-200 border-r border-gray-300 w-12 min-w-[3rem] shadow-[2px_0_6px_-2px_rgba(0,0,0,0.12)]"
                    >
                      No.
                    </th>
                    {RECIPIENT_CSV_LABELS.map((label, colIdx) => (
                      <th
                        key={`h-${colIdx}`}
                        scope="col"
                        title={label}
                        className="sticky top-0 z-20 box-border text-left font-semibold text-gray-700 px-2 py-2 bg-gray-100 border-b border-gray-200 whitespace-nowrap min-w-[6.5rem] w-[6.5rem] truncate shadow-[0_1px_0_0_rgb(229,231,235)]"
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIdx) => {
                    const stripe = rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-100'

                    return (
                      <tr key={`${row.lineNo}-${rowIdx}`} className={stripe}>
                        <td
                          className={`sticky left-0 z-10 box-border px-2 py-1 border-b border-r border-gray-200 text-gray-800 font-mono text-right w-12 min-w-[3rem] ${stripe} shadow-[2px_0_6px_-2px_rgba(0,0,0,0.08)]`}
                        >
                          {row.lineNo}
                        </td>
                        {RECIPIENT_CSV_LABELS.map((_, colIdx) => {
                          const value = row.cells[colIdx] ?? ''
                          const cellErr = cellErrors[`${rowIdx}-${colIdx}`]
                          const label = RECIPIENT_CSV_LABELS[colIdx]
                          const showRed = !!cellErr

                          return (
                            <td
                              key={`r-${rowIdx}-c-${colIdx}`}
                              className={`box-border border-b border-gray-100 align-top min-w-[6.5rem] w-[6.5rem] p-0 ${stripe}`}
                            >
                              <input
                                type="text"
                                value={value}
                                onChange={(event) => updateCell(rowIdx, colIdx, event.target.value)}
                                title={cellErr ? `${label}: ${cellErr}` : label}
                                aria-invalid={showRed}
                                maxLength={STRING_FIELD_MAX_LENGTH[`f${colIdx + 1}`]}
                                className={detailCellInputClass(showRed)}
                              />
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <DetailErrorSummary entries={errorSummary} lineLabel="行" />
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex flex-wrap justify-end gap-3 bg-gray-50">
            <button
              type="button"
              onClick={goBack}
              className="px-5 py-2 border border-gray-300 rounded-lg text-sm hover:bg-white cursor-pointer"
            >
              キャンセル
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 cursor-pointer shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? '保存中...' : '保存'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function buildRows(errors: ImportErrorResponse[]): ErrorRow[] {
  const byRow = new Map<number, {
    rawColumns: string[] | null
    errorColIndexes: Set<number>
    backendReasons: Record<number, string>
    backendErrorCodes: Record<number, string>
  }>()

  for (const err of errors) {
    let entry = byRow.get(err.rowNumber)
    if (!entry) {
      entry = {
        rawColumns: err.rawColumns,
        errorColIndexes: new Set(),
        backendReasons: {},
        backendErrorCodes: {},
      }
      byRow.set(err.rowNumber, entry)
    }

    if (err.rawColumns && !entry.rawColumns) {
      entry.rawColumns = err.rawColumns
    }

    const reason = err.localizedMessage ?? err.errorDetail ?? null
    if (err.fieldKey) {
      const match = err.fieldKey.match(/^f(\d+)$/)
      if (match) {
        const colIdx = Number(match[1]) - 1
        entry.errorColIndexes.add(colIdx)
        if (reason && !entry.backendReasons[colIdx]) {
          entry.backendReasons[colIdx] = reason
        }
        if (err.errorCode && !entry.backendErrorCodes[colIdx]) {
          entry.backendErrorCodes[colIdx] = err.errorCode
        }
      }
    }
  }

  const result: ErrorRow[] = []
  for (const [rowNumber, entry] of byRow) {
    const cells = entry.rawColumns
      ? [...entry.rawColumns]
      : Array.from({ length: RECIPIENT_CSV_LABELS.length }, () => '')

    while (cells.length < RECIPIENT_CSV_LABELS.length) {
      cells.push('')
    }

    result.push({
      lineNo: rowNumber,
      cells,
      initialCells: [...cells],
      errorColIndexes: [...entry.errorColIndexes],
      backendReasons: entry.backendReasons,
      backendErrorCodes: entry.backendErrorCodes,
    })
  }

  result.sort((a, b) => a.lineNo - b.lineNo)
  return result
}
